import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useLanguage } from '@/services/languageService';
import MainLayout from '@/layouts/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Loader2, Download, Facebook, Twitter, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { downloadElementAsImage, shareToSocialMedia } from '@/utils/imageUtils';
import LoadingAnimation from '@/components/LoadingAnimation';
import StaticContent from '@/components/StaticContent';
import {
  loadLocalPokemonDb,
  LocalPokemonEntry,
  getLocalPokemonDb,
  fetchBatchSpeciesData,
  LANGUAGE_CODE_MAPPING,
  Pokemon,
} from '@/services/pokeApi';

// Pokemon types needed for this feature
interface PokemonMatch extends LocalPokemonEntry {
  similarity: number;
  localizedNames?: Record<string, string>;
}

const WhichPokemonAmI: React.FC = () => {
  const { t, currentLanguage } = useLanguage();

  const getLanguageCandidates = useCallback((language: string) => {
    const candidates: string[] = [];
    const mapped = LANGUAGE_CODE_MAPPING[language] || [language];
    mapped.forEach((code) => {
      const normalized = code;
      if (!candidates.includes(normalized)) {
        candidates.push(normalized);
      }
    });
    if (!candidates.includes('en')) {
      candidates.push('en');
    }
    return candidates;
  }, []);
  
  // Determine currentLangCode from currentLanguage provided by useLanguage
  // const langParts = currentLanguage ? currentLanguage.split('-') : ['en']; 
  // const currentLangCode = langParts[0]; // We will use currentLanguage directly for lookup

  const [isLoading, setIsLoading] = useState(false);
  const [matchResult, setMatchResult] = useState<PokemonMatch | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [isDbLoaded, setIsDbLoaded] = useState(false);

  useEffect(() => {
    loadLocalPokemonDb().then(loaded => {
      setIsDbLoaded(loaded);
      if (!loaded) {
        toast.error(t('errorLoadingDb'));
      }
    });
  }, [t]);

  // Define form schema with validation
  const formSchema = z.object({
    name: z.string().min(1, { message: t('nameRequired') }),
    height: z.string().refine(val => {
      const num = Number(val);
      return !isNaN(num) && num > 0 && num <= 300;
    }, { message: t('heightValid') }),
    weight: z.string().refine(val => {
      const num = Number(val);
      return !isNaN(num) && num > 0 && num <= 500;
    }, { message: t('weightValid') }),
  });

  // Initialize the form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      height: '',
      weight: '',
    },
  });

  // Find matching Pokemon based on height and weight using Local DB
  const findMatchingPokemon = async (name: string, heightCm: number, weightKg: number) => {
    if (!isDbLoaded) {
      toast.error(t('errorDbNotReady'));
      const loaded = await loadLocalPokemonDb();
      setIsDbLoaded(loaded);
      if (!loaded) return;
    }

    // 使用 getter 函数获取数据库
    const localDb = getLocalPokemonDb();
    if (!localDb || localDb.length === 0) {
        console.error("Local DB is empty or not accessible via getter.");
        toast.error(t('errorAccessingDb'));
        setIsLoading(false);
        return;
    }

    setIsLoading(true);

    try {
      const heightDm = heightCm / 10; // cm to dm
      const weightHg = weightKg * 10; // kg to hg

      let pokemonWithScores: Array<PokemonMatch> = [];

      for (const pokemonData of localDb) {
        if (pokemonData.height == null || pokemonData.weight == null) {
          console.warn(`Skipping ${pokemonData.name} (ID: ${pokemonData.id}) due to missing height/weight in local DB.`);
          continue;
        }
        
        const heightDiff = Math.abs(pokemonData.height - heightDm);
        const weightDiff = Math.abs(pokemonData.weight - weightHg);

        const normalizedHeightDiff = pokemonData.height > 0 ? heightDiff / pokemonData.height : heightDiff;
        const normalizedWeightDiff = pokemonData.weight > 0 ? weightDiff / pokemonData.weight : weightDiff;

        const distance = Math.sqrt(
          Math.pow(normalizedHeightDiff, 2) + 
          Math.pow(normalizedWeightDiff, 2)
        );

        const similarity = Math.max(0, 100 - distance * 20);

        pokemonWithScores.push({
          ...pokemonData,
          similarity: parseFloat(similarity.toFixed(2))
        });
      }

      if (pokemonWithScores.length === 0) {
        toast.info(t('noPokemonDataFound'));
        setIsLoading(false);
        return;
      }

      pokemonWithScores.sort((a, b) => b.similarity - a.similarity);

      const bestMatchFromLocalDb = pokemonWithScores[0];

      // Prepare for fetching localized names
      const tempPokemonForLocalization = {
        id: bestMatchFromLocalDb.id,
        name: bestMatchFromLocalDb.name,
        // Dummy values for other required Pokemon fields if strictly needed by TS,
        // but fetchBatchSpeciesData primarily uses id/name for lookup and writes localizedNames.
        types: [],
        sprites: { other: { 'official-artwork': { front_default: '' } } },
        stats: [],
        abilities: [],
        moves: [],
        base_experience: 0,
        weight: bestMatchFromLocalDb.weight, // from LocalPokemonEntry
        height: bestMatchFromLocalDb.height, // from LocalPokemonEntry
        species: { url: `https://pokeapi.co/api/v2/pokemon-species/${bestMatchFromLocalDb.id}/` }
      } as Pokemon;

      let finalMatchData: PokemonMatch = { ...bestMatchFromLocalDb, localizedNames: undefined };

      try {
        // This mutates tempPokemonForLocalization by adding localizedNames
        await fetchBatchSpeciesData([tempPokemonForLocalization]);
        finalMatchData.localizedNames = tempPokemonForLocalization.localizedNames;
      } catch (localizationError) {
        console.error(`Failed to fetch localized names for ${bestMatchFromLocalDb.name}:`, localizationError);
        toast.warning(t('errorFetchingLocalizedNamesOptional', {pokemonName: bestMatchFromLocalDb.name}));
        // finalMatchData will not have localizedNames, will use fallback (default name)
      }

      setMatchResult(finalMatchData);
      setUserName(name);
    } catch (error) {
      toast.error(t('errorFinding'));
      console.error('Error finding Pokemon using local DB:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Form submission handler
  const onSubmit = (data: z.infer<typeof formSchema>) => {
    const heightCm = parseFloat(data.height);
    const weightKg = parseFloat(data.weight);
    findMatchingPokemon(data.name, heightCm, weightKg);
  };

  // Handle downloading the result as an image
  const handleDownload = () => {
    toast.info(t('downloadStarted'));
    
    if (!matchResult) return;
    downloadElementAsImage('result-capture-area', `${userName}-${getRawDisplayName(matchResult)}-match.png`)
      .then(success => {
        if (success) {
          toast.success(t('downloadComplete'));
        }
      });
  };

  // Handle sharing on social media
  const handleShare = (platform: 'facebook' | 'twitter' | 'whatsapp') => {
    if (!matchResult) return;
    const text = t('shareText', { name: userName, pokemon: getRawDisplayName(matchResult) });
    shareToSocialMedia(platform, text);
  };

  // Reset the form to try again
  const handleTryAgain = () => {
    setMatchResult(null);
    form.reset();
  };

  // Helper functions to get display names
  const getDisplayName = useCallback((pokemon: PokemonMatch | null): string => {
    if (!pokemon) return '';
    let displayName = pokemon.name; // Fallback
    if (pokemon.localizedNames) {
      const candidates = getLanguageCandidates(currentLanguage);
      for (const candidate of candidates) {
        if (pokemon.localizedNames[candidate]) {
          displayName = pokemon.localizedNames[candidate];
          break;
        }
      }
    }
    return displayName.charAt(0).toUpperCase() + displayName.slice(1);
  }, [currentLanguage, getLanguageCandidates]); // Depend on currentLanguage

  const getRawDisplayName = useCallback((pokemon: PokemonMatch | null): string => {
    if (!pokemon) return '';
    let displayName = pokemon.name;
    if (pokemon.localizedNames) {
      const candidates = getLanguageCandidates(currentLanguage);
      for (const candidate of candidates) {
        if (pokemon.localizedNames[candidate]) {
          displayName = pokemon.localizedNames[candidate];
          break;
        }
      }
    }
    return displayName;
  }, [currentLanguage, getLanguageCandidates]); // Depend on currentLanguage

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-pokemon-blue mb-2">
              {t('whichPokemonAmI')}
            </h1>
            <p className="text-gray-600 mb-8">
              {t('enterHeightWeight')}
            </p>
          </div>
          
          {isLoading ? (
            <Card className="w-full shadow-lg overflow-hidden">
              <CardContent className="pt-6">
                <LoadingAnimation />
              </CardContent>
            </Card>
          ) : !matchResult ? (
            <>
              <Card className="w-full shadow-lg overflow-hidden mb-12">
                <CardHeader className="bg-gradient-to-r from-pokemon-red to-pokemon-blue">
                  <CardTitle className="text-2xl text-center text-white">{t('enterDetails')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('nameLabel')}</FormLabel>
                            <FormControl>
                              <Input placeholder={t('namePlaceholder')} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="height"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('heightLabel')}</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="170" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="weight"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('weightLabel')}</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="65" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <div className="flex justify-center mt-6">
                        <Button 
                          type="submit" 
                          className="bg-pokemon-red hover:bg-red-600 text-white px-6 py-2 rounded-full font-semibold shadow-lg transform transition hover:scale-105"
                        >
                          {t('findMyPokemon')}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </CardContent>
              </Card>
              
              <div className="mt-16 text-gray-700 dark:text-gray-200 animate-fadeIn">
                <div className="mb-12 bg-white dark:bg-slate-700 rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-bold text-pokemon-blue mb-4">{t('aboutTool')}</h2>
                  <StaticContent section="whichPokemonIntro" />
                </div>
                
                <div className="mb-12 bg-white dark:bg-slate-700 rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-bold text-pokemon-blue mb-4">{t('howToUse')}</h2>
                  <StaticContent section="whichPokemonHowToUse" />
                </div>
                
                <div className="mb-12 bg-white dark:bg-slate-700 rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-bold text-pokemon-blue mb-4">{t('benefits')}</h2>
                  <StaticContent section="whichPokemonBenefits" />
                </div>
                
                <div className="mb-12 bg-white dark:bg-slate-700 rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-bold text-pokemon-blue mb-4">{t('faq')}</h2>
                  <StaticContent section="whichPokemonFAQ" />
                </div>
                
                <div className="mb-12 bg-white dark:bg-slate-700 rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-bold text-pokemon-blue mb-4">{t('conclusion')}</h2>
                  <StaticContent section="whichPokemonConclusion" />
                </div>
              </div>
            </>
          ) : (
            <Card className="overflow-hidden shadow-lg" id="result-card">
              <div id="result-capture-area">
                <CardHeader className="bg-gradient-to-r from-pokemon-red to-pokemon-blue p-4">
                  <CardTitle className="text-2xl text-center text-white font-semibold">
                    {userName}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center">
                    <p className="text-lg font-medium text-center mb-4 text-pokemon-blue">
                      {t('whichPokemonMatchesSize')}
                    </p>
                    <p className="text-xl font-bold text-center mb-4">
                      {t('youAreMostLike')} {getDisplayName(matchResult)}
                    </p>
                    <div className="relative w-48 h-48 mb-4">
                      <img 
                        src={matchResult.spriteUrl} 
                        alt={getRawDisplayName(matchResult)} 
                        className="w-full h-full object-contain animate-floating"
                        crossOrigin="anonymous"
                      />
                    </div>
                    <h2 className="text-2xl font-bold capitalize mb-2">
                      {getDisplayName(matchResult)}
                    </h2>
                    <div className="flex gap-2 mb-4">
                      {matchResult.types.map((type) => (
                        <span 
                          key={type} 
                          className={`px-2 py-1 rounded text-xs font-medium capitalize text-white type-${type}`}
                        >
                          {t(type)}
                        </span>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-4 w-full max-w-xs mb-4">
                      <div className="text-center p-3 bg-gray-100 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">{t('heightLabel')}</p>
                        <p className="font-bold text-pokemon-blue">{(matchResult.height / 10).toFixed(1)}m</p>
                      </div>
                      <div className="text-center p-3 bg-gray-100 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">{t('weightLabel')}</p>
                        <p className="font-bold text-pokemon-blue">{(matchResult.weight / 10).toFixed(1)}kg</p>
                      </div>
                    </div>
                    <div className="bg-purple-100 p-4 rounded-lg w-full text-center mb-4 shadow-sm">
                      <p className="text-pokemon-blue font-medium">
                        {t('similarityScore')}: <span className="font-bold">{matchResult.similarity.toFixed(2)}%</span>
                      </p>
                    </div>
                    <div className="text-center text-gray-600 mb-2">
                      <div className="flex items-center justify-center gap-1">
                        <span>{t('madeBy')}</span>
                        <a href="https://randompokemon.games" target="_blank" rel="noopener noreferrer" className="text-pokemon-blue hover:underline">
                          randompokemon.games
                        </a>
                        <Heart size={16} className="text-pokemon-red ml-1 fill-pokemon-red" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </div>
              <CardFooter className="flex flex-col sm:flex-row gap-3 p-4 bg-gray-50 border-t justify-between items-center">
                 <Button 
                   variant="outline" 
                   className="bg-pokemon-blue hover:bg-blue-600 text-white px-12 py-2 rounded-full font-semibold shadow-md transition hover:scale-105 w-full sm:w-auto"
                   onClick={handleDownload}
                 >
                   <Download className="mr-2 h-4 w-4" />
                   {t('downloadImage')}
                 </Button>
                 <div className="flex gap-2">
                   <Button 
                     variant="outline" 
                     size="icon"
                     className="bg-blue-500 text-white hover:bg-blue-600 rounded-full shadow-md transition hover:scale-105"
                     onClick={() => handleShare('facebook')}
                   >
                     <Facebook className="h-4 w-4" />
                   </Button>
                   <Button 
                     variant="outline" 
                     size="icon"
                     className="bg-sky-500 text-white hover:bg-sky-600 rounded-full shadow-md transition hover:scale-105"
                     onClick={() => handleShare('twitter')}
                   >
                     <Twitter className="h-4 w-4" />
                   </Button>
                 </div>
                 <Button 
                   variant="secondary" 
                   className="bg-pokemon-red hover:bg-red-600 text-white px-12 py-2 rounded-full font-semibold shadow-md transition hover:scale-105 w-full sm:w-auto"
                   onClick={handleTryAgain}
                 >
                   {t('tryAgain')}
                 </Button>
              </CardFooter>
            </Card>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default WhichPokemonAmI;
